import type { Statement, StatementSegment } from '@/modules/projects/stores'
import { percentFromRate } from '@/utils'

// One flagged repeat: a cycle whose content repeats an earlier cycle. The detector compares the
// whole project, so the original can live in another statement (the same PDF uploaded twice under
// a different file name), not just earlier pages of the same upload.
export interface DuplicatePageGroup {
  statement: Statement
  duplicate: StatementSegment
  original: StatementSegment | null
  // The statement that owns the original cycle. Same as `statement` for a within-upload repeat.
  originalStatement: Statement | null
}

interface SegmentWithStatement {
  segment: StatementSegment
  statement: Statement
}

// The original cycle a duplicate points at, with the statement that owns it, looked up across the
// whole project. Null when the original is gone (e.g. its statement was deleted).
export const findDuplicateOrigin = (
  statements: Statement[],
  originalSegmentId: number | null | undefined,
): SegmentWithStatement | null => {
  if (originalSegmentId == null) return null
  for (const statement of statements) {
    for (const segment of statement.segments ?? []) {
      if (segment.id === originalSegmentId) return { segment, statement }
    }
  }
  return null
}

// Walk every statement's cycles and build a group for each one tagged a duplicate, resolving the
// original it copies. Pure so the notice bar and review page can both call it off the same store data.
export const duplicatePageGroups = (
  statements: Statement[],
): DuplicatePageGroup[] => {
  const groups: DuplicatePageGroup[] = []
  for (const statement of statements) {
    for (const segment of statement.segments ?? []) {
      if (segment.duplicateOfSegmentId == null) continue
      const origin = findDuplicateOrigin(
        statements,
        segment.duplicateOfSegmentId,
      )
      groups.push({
        statement,
        duplicate: segment,
        original: origin?.segment ?? null,
        originalStatement: origin?.statement ?? null,
      })
    }
  }

  // Stable order: by file, then by where the repeated pages start.
  return groups.sort((first, second) => {
    const byFile = first.statement.fileName.localeCompare(
      second.statement.fileName,
    )
    if (byFile !== 0) return byFile
    return (first.duplicate.startPage ?? 0) - (second.duplicate.startPage ?? 0)
  })
}

// "Pages 11-20" for a cycle, "Page 11" for a single page, or a fallback when no page span was stored.
export const pageRangeLabel = (segment: StatementSegment | null) => {
  if (!segment || segment.startPage == null) return 'unknown pages'
  if (segment.endPage == null || segment.endPage === segment.startPage) {
    return `Page ${segment.startPage}`
  }
  return `Pages ${segment.startPage}-${segment.endPage}`
}

// Where the original lives, for a repeat label: pages alone within the same file, or the pages
// plus the other file's name when the original is a separate upload.
export const originalLocationLabel = (group: DuplicatePageGroup): string => {
  const pages = pageRangeLabel(group.original)
  if (
    group.originalStatement &&
    group.originalStatement.id !== group.statement.id
  ) {
    return `${pages} of ${group.originalStatement.fileName}`
  }
  return pages
}

// Plain-English version of the engine's match tier.
export const confidenceLabel = (confidence: 'exact' | 'near' | null) => {
  if (confidence === 'exact') return 'Exact match'
  if (confidence === 'near') return 'Likely match'
  return 'Possible match'
}

// The 0..1 overlap score as a whole percentage, e.g. "98% similar". Null when the engine stored none.
export const similarityLabel = (similarity: number | null) =>
  similarity == null ? null : `${percentFromRate(similarity)} similar`
